import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Alert,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";

//Services
import ChefForPartyService from "../../services/ChefForPartyService";

//Events
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";

//Componets
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";

//Types
interface Props {
  open: boolean;
  mealType: string;
  partyId: number;
  existingDishes: any[];
  editingDish?: any;
  onClose: () => void;
  onSuccess: () => void;
}

const chefForPartyService = new ChefForPartyService();

const dishFilterOptions = (options: any[], { inputValue }: { inputValue: string }) => {
  const q = inputValue.trim().toLowerCase();
  if (!q) return options;
  const matched = options.filter((o) => o?.name?.toLowerCase().includes(q));
  return matched.sort((a, b) => {
    const aStarts = a.name.toLowerCase().startsWith(q);
    const bStarts = b.name.toLowerCase().startsWith(q);
    if (aStarts === bStarts) return 0;
    return aStarts ? -1 : 1;
  });
};


const AddDishDialog = ({
  open,
  mealType,
  partyId,
  existingDishes,
  editingDish,
  onClose,
  onSuccess,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [customDish, setCustomDish] = useState(false);

  const [category, setCategory] = useState<any[]>([]);
  const [selectCategory, setSelectCategory] = useState<any>(null);
  const [allDishes, setAllDishes] = useState<any[]>([]);
  const [allDishesInCategory, setAllDishesInCategory] = useState<any[]>([]);
  const [selectedDish, setSelectedDish] = useState<any>(null);

  const [dishName, setDishName] = useState("");
  const [type, setType] = useState("");
  const [customCategory, setCustomCategory] = useState<any>(null);

  useEffect(() => {
    if (!open) return;

    if (editingDish) {
      setDishName(editingDish.dishName || "");
      setType(editingDish.type || "");
      setCustomCategory(editingDish.categoryId || null);
      setCustomDish(true);
      setSelectedDish(editingDish);
    } else {
      resetState();
    }

    chefForPartyService.getAllCategory()
      .then((data: any) => setCategory(data))
      .catch(() => setCategory([]));

    chefForPartyService.getAllDishes()
      .then((data: any) => setAllDishes(data?.data || []))
      .catch(() => setAllDishes([]));
  }, [open, editingDish]);

  useEffect(() => {
    if (!selectCategory) {
      setAllDishesInCategory(allDishes);
      return;
    }

    chefForPartyService
      .getAllDishesForTypeAndCategory(selectCategory, mealType)
      .then((data: any) => setAllDishesInCategory(data))
      .catch(() => setAllDishesInCategory([]));
  }, [selectCategory, mealType, allDishes]);

  const resetState = () => {
    setCustomDish(false);
    setSelectCategory(null);
    setSelectedDish(null);
    setDishName("");
    setType("");
    setCustomCategory(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSubmit = async () => {
    if (!customDish && !selectedDish) {
      showError("Please select a dish");
      return;
    }

    if (customDish && (!dishName || !type || !customCategory)) {
      showError("Please fill all custom dish details");
      return;
    }

    const dishData = customDish
      ? {
          dishName,
          type,
          mealType,
          categoryId: customCategory,
        }
      : selectedDish;

    try {
      setLoading(true);

      let updatedDishes;
      if (editingDish) {
        // Edit mode: replace the existing dish
        updatedDishes = existingDishes.map((dish: any) =>
          dish === editingDish ? dishData : dish
        );
      } else {
        // Add mode: check for duplicates and add new dish
        const duplicate = existingDishes.find(
          (d: any) => d.dishName === dishData.dishName
        );

        if (duplicate) {
          showError("Dish already added");
          return;
        }

        updatedDishes = [...existingDishes, dishData];
      }

      await chefForPartyService.addDIshChefForParty(partyId, {
        dishes: updatedDishes,
      });

      showSuccess(editingDish ? "Dish updated successfully" : "Dish added successfully");
      onSuccess();
      handleClose();
      
    } catch (error) {
      showError(editingDish ? "Failed to update dish" : "Failed to add dish");
    } finally {
      setLoading(false);
    }
  };

  const showError = (message: string) => {
    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
      message,
      type: CustomSnackbarTypes.ERROR,
    });
  };

  const showSuccess = (message: string) => {
    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
      message,
      type: CustomSnackbarTypes.SUCCESS,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between" }}
      >
        {editingDish ? "Edit Dish" : "Add Dish"}
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3} mt={1}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={customDish}
                  onChange={() => setCustomDish(true)}
                />
              }
              label={editingDish ? "Custom Dish (Edit Mode)" : "Custom Dish"}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!customDish}
                  onChange={() => setCustomDish(false)}
                />
              }
              label={editingDish ? "Select From Category (Edit Mode)" : "Select From Category"}
            />
          </FormGroup>

          {!customDish && (
            <>
              <FormControl fullWidth>
                <InputLabel>Cuisine</InputLabel>
                <Select
                  value={selectCategory || ""}
                  label="Cuisine"
                  onChange={(e: any) =>
                    setSelectCategory(e.target.value)
                  }
                >
                  {category.map((c: any) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Autocomplete
                options={allDishesInCategory}
                getOptionLabel={(option: any) => option?.name ?? ""}
                filterOptions={dishFilterOptions}
                value={selectedDish}
                onChange={(_e, newValue) => setSelectedDish(newValue)}
                isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={option?.id}>
                    {option?.name} <span style={{ color: "#888", marginLeft: 6, fontSize: 12 }}>({option?.mealType})</span>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Search Dish Name" />
                )}
              />
            </>
          )}

          {customDish && (
            <>
              {editingDish ? (
                <Alert severity="info">
                  You are editing: <strong>{editingDish.dishName}</strong>. Modify the details below and submit to update.
                </Alert>
              ) : (
                <Alert severity="warning">
                  Ingredients will not be auto-added for custom dish.
                </Alert>
              )}

              <TextField
                label="Dish Name"
                value={dishName}
                onChange={(e) => setDishName(e.target.value)}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={type}
                  label="Type"
                  onChange={(e: any) =>
                    setType(e.target.value)
                  }
                >
                  <MenuItem value="VEG">Veg</MenuItem>
                  <MenuItem value="NON_VEG">Non-Veg</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Cuisine</InputLabel>
                <Select
                  value={customCategory || ""}
                  label="Cuisine"
                  onChange={(e: any) =>
                    setCustomCategory(e.target.value)
                  }
                >
                  {category.map((c: any) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {editingDish ? "Update" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDishDialog;
